import { MMKV } from 'react-native-mmkv';

import { getRuntimeClientBuild } from '../runtime/clientBuild';

export const CHECKLIST_VALUES_V2_FEATURE = 'checklist_values_v2';
export const CHECKLIST_SNAPSHOTS_FEATURE = 'checklist_snapshots_v1';
export const SYNC_TOMBSTONES_FEATURE = 'sync_tombstones';
export const HISTORICAL_SNAPSHOTS_FEATURE = 'historical_snapshots';
export const GPS_ADDRESS_FEATURE = 'gps_address_v1';

export const STRUCTURAL_SYNC_FEATURES = [
  CHECKLIST_SNAPSHOTS_FEATURE,
  CHECKLIST_VALUES_V2_FEATURE,
  HISTORICAL_SNAPSHOTS_FEATURE,
  SYNC_TOMBSTONES_FEATURE,
  GPS_ADDRESS_FEATURE,
] as const;

export interface SyncCanaryFeatureGateway {
  isEnabled(input: {
    companyId: string;
    featureKey: string;
    clientBuild: number;
  }): Promise<boolean>;
}

export interface SyncCanaryFeatureDecisionStore {
  get(input: { companyId: string; featureKey: string; clientBuild: number }): boolean | null;
  set(input: {
    companyId: string;
    featureKey: string;
    clientBuild: number;
    enabled: boolean;
  }): void;
}

export interface SyncCanaryFeatureDecision {
  companyId: string;
  featureKey: string;
  clientBuild: number;
  enabled: boolean;
}
const decisionListeners = new Set<(decision: SyncCanaryFeatureDecision) => void>();
/** Permite que a UI revogue edição quando a sincronização observar a flag OFF. */
export const subscribeSyncCanaryFeatureDecisions = (listener: (decision: SyncCanaryFeatureDecision) => void): (() => void) => {
  decisionListeners.add(listener);
  return () => { decisionListeners.delete(listener); };
};

interface StringStorage {
  getString(key: string): string | undefined;
  set(key: string, value: string): void;
}

const decisionKey = (input: {
  companyId: string;
  featureKey: string;
  clientBuild: number;
}): string => [
  'structural-feature-v1',
  encodeURIComponent(input.companyId),
  input.clientBuild,
  encodeURIComponent(input.featureKey),
].join(':');

export class MMKVSyncCanaryFeatureDecisionStore implements SyncCanaryFeatureDecisionStore {
  private storage: StringStorage | null = null;

  constructor(private readonly createStorage = (): StringStorage => (
    new MMKV({ id: 'app-guincho-structural-feature-decisions' })
  )) {}

  get(input: { companyId: string; featureKey: string; clientBuild: number }): boolean | null {
    const value = this.getStorage().getString(decisionKey(input));
    if (value === 'enabled') return true;
    if (value === 'disabled') return false;
    return null;
  }

  set(input: {
    companyId: string;
    featureKey: string;
    clientBuild: number;
    enabled: boolean;
  }): void {
    this.getStorage().set(decisionKey(input), input.enabled ? 'enabled' : 'disabled');
    for (const listener of decisionListeners) {
      try { listener({ ...input }); } catch { /* A observação da UI não interrompe a decisão de sync. */ }
    }
  }

  private getStorage(): StringStorage {
    if (!this.storage) this.storage = this.createStorage();
    return this.storage;
  }
}

export const syncCanaryFeatureDecisionStore = new MMKVSyncCanaryFeatureDecisionStore();

export const getSyncClientBuild = getRuntimeClientBuild;

export class SyncCanaryFeatureService {
  constructor(
    private readonly gateway: SyncCanaryFeatureGateway,
    private readonly enabled = true,
    private readonly decisionStore?: SyncCanaryFeatureDecisionStore
  ) {}

  async isEnabled(companyId: string, featureKey: string, clientBuild: number): Promise<boolean> {
    if (!this.enabled) return false;

    const normalizedCompanyId = companyId.trim();
    const normalizedFeatureKey = featureKey.trim();
    if (!normalizedCompanyId || !normalizedFeatureKey || clientBuild <= 0) {
      throw new Error('Escopo invalido para consultar feature de sync canario.');
    }

    const decision = await this.gateway.isEnabled({
      companyId: normalizedCompanyId,
      featureKey: normalizedFeatureKey,
      clientBuild,
    });
    this.decisionStore?.set({
      companyId: normalizedCompanyId,
      featureKey: normalizedFeatureKey,
      clientBuild,
      enabled: decision,
    });
    return decision;
  }

  getPersistedDecision(companyId: string, featureKey: string, clientBuild: number): boolean | null {
    if (!this.enabled) return false;

    const normalizedCompanyId = companyId.trim();
    const normalizedFeatureKey = featureKey.trim();
    if (!normalizedCompanyId || !normalizedFeatureKey || clientBuild <= 0) return null;
    return this.decisionStore?.get({
      companyId: normalizedCompanyId,
      featureKey: normalizedFeatureKey,
      clientBuild,
    }) ?? null;
  }

  async refresh(
    companyId: string,
    featureKeys: readonly string[],
    clientBuild: number
  ): Promise<Record<string, boolean | null>> {
    const decisions = await Promise.all(featureKeys.map(async featureKey => [
      featureKey,
      await this.refreshOne(companyId, featureKey, clientBuild),
    ] as const));
    return Object.fromEntries(decisions);
  }

  private async refreshOne(
    companyId: string,
    featureKey: string,
    clientBuild: number
  ): Promise<boolean | null> {
    try {
      return await this.isEnabled(companyId, featureKey, clientBuild);
    } catch {
      try {
        return this.getPersistedDecision(companyId, featureKey, clientBuild);
      } catch {
        return null;
      }
    }
  }

  async isEnabledOrFalse(
    companyId: string,
    featureKey: string,
    clientBuild: number,
    onUnavailable?: () => void
  ): Promise<boolean> {
    try {
      return await this.isEnabled(companyId, featureKey, clientBuild);
    } catch {
      onUnavailable?.();
      return false;
    }
  }
}
